import * as THREE from "three";
import { Polygon, Solid, Vector3 } from "opengeometry";

import { IShape } from "../../shapes/base-type";
import { ElementType } from "../base-type";
import { OPElement, OPGeometry, IPlaceable } from "../op-element";
import type { Vec3 } from "../../types";
import { Event } from "../../../../../utils/event";
import { WallFrame, localToWorld } from "../solids/wall-frame";
import { rotatePoints, degToRad, Y_UP, centroid as centroidOf } from "../../../../../utils/transform";

export interface OpeningOptions<T extends ElementType = ElementType.OPENING> {
  ogid?: string;
  labelName: string;
  type: T;
  hostWallId?: string;
  // Geometry fields are optional at the type level so subclasses (DoorOptions,
  // WindowOptions) can extend OpeningOptions without redeclaring fields they
  // do not use. Plain Opening always populates them via the constructor's
  // default propertySet, so the `!` assertions in this class are sound.
  thickness?: number;
  height?: number;
  baseHeight?: number;
  // Wall-local coordinates. +Y up; walls live in the XZ plane.
  //   points[i] = [u, v, h] where
  //     u — distance along the wall from its start,
  //     v — perpendicular offset in the XZ plane (normal to the wall),
  //     h — vertical offset above the wall's base.
  // When no host frame has been bound, points are interpreted as world
  // (x, y, z) — the unhosted/legacy path.
  length?: number; // Optional length property for convenience, can be derived from points if not provided

  points?: [number, number, number][];
}

export class Opening<T extends ElementType = ElementType.OPENING> extends OPElement implements IShape, IPlaceable {
  ogType: T = ElementType.OPENING as T;
  ogid: string;

  subElements2D: Map<string, OPGeometry> = new Map();
  private isProfileView: boolean = false;

  subElements3D: Map<string, OPGeometry> = new Map();
  private isModelView: boolean = false;

  selected = false;
  edit = false;
  locked = false;

  _outlineEnabled: boolean = false;
  onOpeningUpdated: Event<null> = new Event();

  /** Host wall's local coordinate frame. Null until bound via bindHostFrame. */
  protected hostFrame: WallFrame | null = null;

  // Semantic Properties
  propertySet: OpeningOptions<T> = {
    labelName: "Standard Opening",
    type: ElementType.OPENING as T,
    thickness: 0.3,
    height: 1,
    baseHeight: 0,
    // Wall-local (u, v, h). Interpreted as world (x, y, z) until bindHostFrame is called.
    points: [
      [0, 0, 0],
      [1.5, 0, 0],
    ],
  };

  /**
   * Translate this opening by `delta`.
   *
   * For UNHOSTED openings (no host wall frame bound): `delta` is world-space.
   * For HOSTED openings: `delta` is in wall-local (u, v, h) coordinates —
   * `[alongWall, acrossWall, elevation]`. Most callers should reposition
   * hosted openings via `polyWall.moveDoor()` / `polyWall.moveWindow()`
   * instead of calling `move()` directly.
   */
  move(delta: Vec3): void {
    this.propertySet.points = (this.propertySet.points ?? []).map(
      (p) => [p[0] + delta[0], p[1] + delta[1], p[2] + delta[2]] as [number, number, number],
    );
    this.setOPGeometry();
  }

  rotate(angleDeg: number, axis: Vec3 = Y_UP, anchor?: Vec3): void {
    if (this.hostFrame) {
      console.warn("Opening.rotate is a no-op for hosted openings; rotation is constrained by the host wall.");
      return;
    }
    const points = this.propertySet.points ?? [];
    const pivot = anchor ?? centroidOf(points as Vec3[]);
    const rotated = rotatePoints(points as Vec3[], degToRad(angleDeg), axis, pivot);
    this.propertySet.points = rotated.map((p) => [p[0], p[1], p[2]] as [number, number, number]);
    this.setOPGeometry();
  }

  get outline() { return this._outlineEnabled; }
  set outline(value: boolean) {
    this._outlineEnabled = value;
    for (const obj of this.subElements2D.values()) {
      obj.outline = value;
    }
    for (const obj of this.subElements3D.values()) {
      obj.outline = value;
    }
  }

  get labelName() { return this.propertySet.labelName; }
  set labelName(value: string) {
    this.propertySet.labelName = value;
  }

  get thickness(): number { return this.propertySet.thickness!; }
  set thickness(value: number) {
    this.propertySet.thickness = Math.max(0.1, value);
    this.setOPGeometry();
  }

  get height(): number { return this.propertySet.height!; }
  set height(value: number) {
    this.propertySet.height = Math.max(0.1, value);
    this.setOPGeometry();
  }

  get baseHeight(): number { return this.propertySet.baseHeight!; }
  set baseHeight(value: number) {
    this.propertySet.baseHeight = Math.max(0, value);
    this.setOPGeometry();
  }

  get start(): [number, number, number] { return this.propertySet.points![0]; }
  set start(value: [number, number, number]) {
    this.propertySet.points![0] = value;
    this.setOPGeometry();
  }

  get end(): [number, number, number] { return this.propertySet.points![1]; }
  set end(value: [number, number, number]) {
    this.propertySet.points![1] = value;
    this.setOPGeometry();
  }

  get profileView() { return this.isProfileView; }
  set profileView(value: boolean) {
    this.isProfileView = value;
    for (const obj of this.subElements2D.values()) {
      obj.visible = value;
    }
  }

  get modelView() { return this.isModelView; }
  set modelView(value: boolean) {
    this.isModelView = value;
    for (const [key, obj] of this.subElements3D.entries()) {
      if (key === 'hole-base-3d') {
        // 3D extrusion seed; never rendered.
        obj.visible = false;
      } else {
        obj.visible = value;
      }
    }
  }

  /** Plan-view hole loop (Vector3[] in the XZ plane at Y=0). The wall uses this as a Polygon hole.
   *  The 1 µm across-wall inset keeps hole edges strictly inside the outer polygon so the WASM
   *  triangulator does not produce degenerate output when edges coincide with the boundary. */
  get holeLoop2D(): Vector3[] {
    const worldPoints = this.toWorldPoints();
    if (worldPoints.length < 2) return [];

    const start = worldPoints[0];
    const end   = worldPoints[1];
    const dir   = new THREE.Vector3(end.x - start.x, end.y - start.y, end.z - start.z).normalize();
    const perp  = new THREE.Vector3(0, 1, 0).cross(dir);
    const t2    = this.propertySet.thickness! / 2 - 1e-6;

    return [
      new Vector3(start.x + perp.x * t2, 0, start.z + perp.z * t2),
      new Vector3(end.x   + perp.x * t2, 0, end.z   + perp.z * t2),
      new Vector3(end.x   - perp.x * t2, 0, end.z   - perp.z * t2),
      new Vector3(start.x - perp.x * t2, 0, start.z - perp.z * t2),
    ];
  }

  get opening3D(): Solid {
    return this.subElements3D.get(this.ogid + '-3d') as Solid;
  }

  constructor(openingConfig?: Partial<OpeningOptions<T>>) {
    super();
    this.ogid = openingConfig?.ogid ?? crypto.randomUUID();

    this.subElements2D = new Map<string, OPGeometry>();
    this.subElements3D = new Map<string, OPGeometry>();

    if (openingConfig) {
      this.propertySet = {
        ...this.propertySet,
        ...openingConfig
      };
    }

    this.propertySet.ogid = this.ogid;
    this.setOPGeometry();
  }

  setOPConfig(config: OpeningOptions<T>): void {
    this.dispose();

    this.propertySet = {
      ...this.propertySet,
      ...config,
    };

    this.setOPGeometry();
    this.setOPMaterial();
  }

  getOPConfig(): OpeningOptions<T> {
    return this.propertySet;
  }

  /**
   * Bind this opening to its host wall's frame. After binding, propertySet.points
   * are interpreted as wall-local (alongWall, acrossWall, elevation) and
   * transformed to world on demand. Pass `null` to unhost the opening — its
   * geometry then renders in identity coordinates (alongWall→x, elevation→y,
   * acrossWall→z).
   */
  bindHostFrame(frame: WallFrame | null): void {
    this.hostFrame = frame;
    this.setOPGeometry();
  }

  /**
   * Convert stored points to world-space Vector3s.
   * If a host frame is bound: points are (u, v, h) local → world.
   * Otherwise: points are already world (x, y, z).
   */
  toWorldPoints(): Vector3[] {
    const frame = this.hostFrame;
    if (!frame) {
      return this.propertySet.points!.map(
        ([x, y, z]) => new Vector3(x, y, z),
      );
    }
    return this.propertySet.points!.map(
      ([u, v, h]) => localToWorld(frame, u, v, h),
    );
  }

  dispose() {
    for (const obj of this.subElements2D.values()) {
      const mesh = obj as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach(mat => mat.dispose());
      } else if (mesh.material) {
        mesh.material.dispose();
      }

      mesh.removeFromParent();
    }
    for (const obj of this.subElements3D.values()) {
      const mesh = obj as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      if (Array.isArray(mesh.material)) {
        mesh.material.forEach(mat => mat.dispose());
      } else if (mesh.material) {
        mesh.material.dispose();
      }

      mesh.removeFromParent();
    }

    this.subElements2D.clear();
    this.subElements3D.clear();
  }

  setOPGeometry(): void {
    this.dispose();

    const worldPoints = this.toWorldPoints();
    if (worldPoints.length < 2) {
      return;
    }

    const start = worldPoints[0];
    const end   = worldPoints[1];
    const dir   = new THREE.Vector3(end.x - start.x, end.y - start.y, end.z - start.z).normalize();
    const perp  = new THREE.Vector3(0, 1, 0).cross(dir);
    const t2    = this.propertySet.thickness! / 2;

    // Four polygon vertices — winding matches the old getOffset result:
    // [off1.start, off1.end, off2.end, off2.start]
    const elevatedFootprint: Vector3[] = [
      new Vector3(start.x + perp.x * t2, start.y + perp.y * t2, start.z + perp.z * t2),
      new Vector3(end.x   + perp.x * t2, end.y   + perp.y * t2, end.z   + perp.z * t2),
      new Vector3(end.x   - perp.x * t2, end.y   - perp.y * t2, end.z   - perp.z * t2),
      new Vector3(start.x - perp.x * t2, start.y - perp.y * t2, start.z - perp.z * t2),
    ];

    // ── 3D extrusion seed at the line's actual elevation ────────────────────
    // Extruded vertically by `height` so the resulting solid spans
    // [lineElevation, lineElevation + height] for boolean subtraction from
    // the wall's volume.
    const polygon3DBase = new Polygon({
      vertices: elevatedFootprint,
      color: 0xffcccc,
    });
    polygon3DBase.outline = false;
    polygon3DBase.visible = false;     // never rendered; just an extrusion seed.
    this.subElements3D.set('hole-base-3d', polygon3DBase);
    this.add(polygon3DBase);

    const extrudedShape = polygon3DBase.extrude(this.propertySet.height!);
    extrudedShape.ogid = this.ogid + '-3d';
    this.subElements3D.set(extrudedShape.ogid, extrudedShape);
    this.add(extrudedShape);

    this.onOpeningUpdated.trigger(null);
  }

  setOPMaterial(): void {
    
  }
}
